import { useState, useEffect, useMemo, useRef } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { CheckCircle, XCircle, CreditCard, Plus } from "phosphor-react";
import { useStripePayment } from "@/hooks/useStripePayment";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import {
  StripeService,
  type PaymentIntentItem,
  type PaymentIntentBooking,
} from "@/services/stripe.service";
import { loadStripe } from "@stripe/stripe-js";

// Stripe will be loaded with platform publishable key + stripeAccount parameter
// Backend uses platform secret key with stripeAccount header for payment processing

type PaymentStatus = "idle" | "processing" | "success" | "error";

const DEFAULT_SUBMIT_LABEL = "Confirm and Book";

interface CheckoutFormProps {
  readonly email: string;
  readonly submitLabel?: string;
  readonly onPaymentSuccess?: (externalPaymentId: string) => void;
  readonly onPaymentError?: (message: string) => void;
}

function CheckoutForm({
  email,
  submitLabel = DEFAULT_SUBMIT_LABEL,
  onPaymentSuccess,
  onPaymentError,
}: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [externalPaymentId, setExternalPaymentId] = useState("");

  /* Every failure ends here, and every failure says something. A
     charge that does not complete must never leave the member on a
     spinner or drop them back to an unchanged form with no
     explanation — that reads as "the page just reloaded" and is
     indistinguishable from a bug. */
  const failPayment = (message: string) => {
    // A caller that owns the failure (the booking flow has a booking to
    // roll back) gets told and shows its own message; the form goes back
    // to idle rather than stacking a second error screen behind it.
    if (onPaymentError) {
      setPaymentStatus("idle");
      onPaymentError(message);
      return;
    }
    setErrorMsg(message);
    setPaymentStatus("error");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (paymentStatus === "processing") return;
    /* Not a silent return: with Stripe uninitialised — SDK blocked,
       key rejected — the submit button would otherwise do nothing at
       all, with nothing on screen to explain why. */
    if (!stripe || !elements) {
      failPayment(
        "We couldn't load the payment provider. Check your connection and try again.",
      );
      return;
    }

    setPaymentStatus("processing");
    setErrorMsg("");

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          receipt_email: email,
        },
        redirect: "if_required",
      });

      if (error) {
        failPayment(error.message || "An error occurred");
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        setExternalPaymentId(paymentIntent.id);
        setPaymentStatus("success");
        // Call success callback with payment intent ID
        if (onPaymentSuccess) {
          onPaymentSuccess(paymentIntent.id);
        }
        return;
      }

      /* Anything else — a requires_action that `redirect: 'if_required'`
         could not resolve, processing, canceled, requires_payment_method
         — is not a completed payment. Without this branch the form sat
         on "Processing…" for ever. */
      failPayment("The payment did not complete. Please try again.");
    } catch {
      /* confirmPayment rejects rather than resolving when the network
         drops mid-confirm. */
      failPayment(
        "The payment was interrupted before it completed. You have not been charged — please try again.",
      );
    }
  };

  const handleReset = () => {
    setPaymentStatus("idle");
    setErrorMsg("");
    setExternalPaymentId("");
  };

  // Show success state
  if (paymentStatus === "success") {
    return (
      <div style={styles.form}>
        <div style={styles.successIcon}>
          <CheckCircle size={64} color="#10b981" weight="fill" />
        </div>
        <h2 style={styles.successTitle}>Payment Successful!</h2>
        <p style={styles.successMessage}>
          Your payment has been processed successfully.
        </p>
        {externalPaymentId && (
          <div style={styles.paymentDetails}>
            <p style={styles.detailLabel}>Payment ID</p>
            <p style={styles.detailValue}>{externalPaymentId}</p>
          </div>
        )}
        <button onClick={handleReset} style={styles.button}>
          Make Another Payment
        </button>
      </div>
    );
  }

  // Show error state
  if (paymentStatus === "error") {
    return (
      <div style={styles.form}>
        <div style={styles.errorIcon}>
          <XCircle size={64} color="#ef4444" weight="fill" />
        </div>
        <h2 style={styles.errorTitle}>Payment Failed</h2>
        <p style={styles.errorMessage}>{errorMsg}</p>
        <button onClick={handleReset} style={styles.button}>
          Try Again
        </button>
      </div>
    );
  }

  // Show payment form
  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <div style={styles.emailRow}>
        <span style={styles.emailLabel}>Email: </span>
        <span style={styles.emailValue}> {email}</span>
      </div>

      <p style={styles.sectionLabel}>Payment method</p>

      <div style={styles.paymentElementWrapper}>
        <PaymentElement options={{ layout: "tabs" }} />
      </div>

      <button
        type="submit"
        disabled={!stripe || paymentStatus === "processing"}
        style={{ 
          ...styles.button, 
          opacity: !stripe || paymentStatus === "processing" ? 0.6 : 1 
        }}
      >
        {paymentStatus === "processing" ? "Processing…" : submitLabel}
      </button>

      

      <p style={styles.poweredBy}>
        Powered by <strong>stripe</strong>
      </p>
    </form>
  );
}

/**
 * Payment is impossible at this location. Reports upward once on mount
 * (an effect, so it never fires during render) and draws nothing — the
 * caller shows the message and releases whatever it was holding.
 */
function UnconfiguredGateway({ onReport }: { readonly onReport: () => void }) {
  const reported = useRef(false);
  useEffect(() => {
    if (reported.current) return;
    reported.current = true;
    onReport();
  }, [onReport]);
  return null;
}

interface StripePaymentFormProps {
  readonly email?: string;
  readonly amount?: number;
  readonly currency?: string;
  readonly memberId: string;
  readonly locationId: string;
  /** Cart contents for a shop purchase — recorded on the Stripe charge
   *  so the payment says what was bought. Omitted for bookings. */
  readonly items?: PaymentIntentItem[];
  /** The booking being paid for, when this is a drop-in fee. Omitted for shop. */
  readonly booking?: PaymentIntentBooking;
  /** CTA label on the pay button — defaults to the booking wording. */
  readonly submitLabel?: string;
  readonly onPaymentSuccess?: (externalPaymentId: string) => void;
  /** Called instead of the built-in error screen when the caller owns the
   *  failure path (e.g. it must cancel a booking it already created). */
  readonly onPaymentError?: (message: string) => void;
}

export default function StripePaymentForm({
  email = "",
  amount = 1000,
  currency = "usd",
  memberId,
  locationId,
  items,
  booking,
  submitLabel = DEFAULT_SUBMIT_LABEL,
  onPaymentSuccess,
  onPaymentError,
}: StripePaymentFormProps) {
  const [isPaymentInitialized, setIsPaymentInitialized] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | null>(null);
  const [showAddNewCard, setShowAddNewCard] = useState(false);
  const [isProcessingSavedCard, setIsProcessingSavedCard] = useState(false);
  /* Saved-card failures used to be a toast, which slides away and
     leaves the member looking at an unchanged card list — the same
     "nothing happened" as no message at all. They now get the same
     Payment Failed panel the new-card path shows. */
  const [savedCardError, setSavedCardError] = useState<string | null>(null);
  const [isStripeConfigured, setIsStripeConfigured] = useState<boolean | null>(null);
  const [isCheckingConfig, setIsCheckingConfig] = useState(true);
  // Once payment succeeds we must fully unmount Stripe <Elements> so no Stripe
  // UI (form, CTA, or the Stripe dev toolbar from an orphaned Elements
  // provider) can leak onto the order-success page.
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  // Check if Stripe is configured for this location on mount
  useEffect(() => {
    const checkStripeConfig = async () => {
      try {
        const response = await StripeService.checkConfiguration(locationId);
        setIsStripeConfigured(response.isConfigured);
      } catch (error) {
        console.error('Failed to check Stripe configuration:', error);
        setIsStripeConfigured(false);
      } finally {
        setIsCheckingConfig(false);
      }
    };

    checkStripeConfig();
  }, [locationId]);

  // Fetch saved payment methods only if Stripe is configured
  const { data: paymentMethodsData } = usePaymentMethods({
    memberId,
    locationId,
    enabled: isStripeConfigured === true,
  });

  const savedPaymentMethods = paymentMethodsData?.paymentMethods || [];

  const { clientSecret, isLoading, error, publishableKey } = useStripePayment({
    amount,
    currency,
    memberId,
    locationId,
    items,
    booking,
    enabled: isPaymentInitialized || showAddNewCard,
  });

  // Memoize the Stripe promise to avoid re-creating on every render
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  // Cleanup handler after successful payment
  const handlePaymentSuccess = (externalPaymentId: string) => {
    // Tear down Stripe immediately on success — the real success UI is the
    // separate /shop-order-success route, not anything Stripe renders.
    setPaymentCompleted(true);
    if (onPaymentSuccess) {
      onPaymentSuccess(externalPaymentId);
    }
  };

  /* One exit for every saved-card failure: drop the spinner, then
     either hand the failure to a caller that owns it or show the
     Payment Failed panel. Nothing returns quietly. */
  const failSavedCard = (message: string) => {
    setIsProcessingSavedCard(false);
    if (onPaymentError) {
      onPaymentError(message);
      return;
    }
    setSavedCardError(message);
  };

  // Handle payment with saved payment method
  const handlePayWithSavedCard = async () => {
    if (!selectedPaymentMethodId) return;

    setIsProcessingSavedCard(true);
    setSavedCardError(null);

    try {
      // If we don't have payment intent yet, create it
      let secret = clientSecret;
      let pubKey = publishableKey;

      if (!secret || !pubKey) {
        // Fetch payment intent
        const response = await StripeService.createPaymentIntent({
          amount,
          currency,
          memberId,
          locationId,
          items,
          booking,
        });
        
        secret = response.clientSecret;
        pubKey = response.publishableKey;
      }

      // Reuse the memoized stripe instance if key matches, otherwise load fresh
      const stripeInstance = pubKey === publishableKey && stripePromise
        ? await stripePromise
        : await loadStripe(pubKey);

      if (!stripeInstance) {
        failSavedCard(
          "We couldn't load the payment provider. Check your connection and try again.",
        );
        return;
      }

      const { error, paymentIntent } = await stripeInstance.confirmCardPayment(secret, {
        payment_method: selectedPaymentMethodId,
      });

      if (error) {
        failSavedCard(error.message || 'Payment failed');
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        handlePaymentSuccess(paymentIntent.id);
        setIsProcessingSavedCard(false);
        return;
      }

      /* Not succeeded and not an error — requires_action, processing,
         canceled. Without this branch the screen sat on "Processing
         Payment" for ever with nothing to tap. */
      failSavedCard('The payment did not complete. Please try again.');
    } catch (err) {
      failSavedCard('Payment failed. Please try again.');
    }
  };

  /* Saved-card failure — the same panel the new-card path shows, so a
     decline looks like a decline whichever card it was. Try Again
     returns to the card list with the selection intact. */
  if (savedCardError) {
    return (
      <div style={styles.form}>
        <div style={styles.errorIcon}>
          <XCircle size={64} color="#ef4444" weight="fill" />
        </div>
        <h2 style={styles.errorTitle}>Payment Failed</h2>
        <p style={styles.errorMessage}>{savedCardError}</p>
        <button onClick={() => setSavedCardError(null)} style={styles.button}>
          Try Again
        </button>
      </div>
    );
  }

  // Show processing state when paying with saved card
  if (isProcessingSavedCard) {
    return (
      <div style={styles.form}>
        <h2 style={styles.successTitle}>Processing Payment</h2>
        <p style={styles.loading}>Please wait while we process your payment...</p>
      </div>
    );
  }

  // Show loading while checking Stripe configuration
  if (isCheckingConfig) {
    return <p style={styles.loading}>Loading payment options...</p>;
  }

  // Show error if Stripe is not configured for this location
  if (isStripeConfigured === false) {
    // A caller that created a booking ahead of the charge is told, so it
    // can hand the slot back — this location can never take the payment,
    // so there is nothing to wait for. Rendered only when nobody owns
    // the failure (cart checkout, which has no booking to release).
    if (onPaymentError) {
      return (
        <UnconfiguredGateway
          onReport={() =>
            onPaymentError(
              'This location is not set up to accept payments yet. Please contact support@tbx.golf.',
            )
          }
        />
      );
    }
    return (
      <div style={styles.form}>
        <p style={styles.errorMessage}>
          Payment Gateway has not been configured. Please contact{' '}
          <a href="mailto:support@tbx.golf" style={styles.supportLink}>
            support@tbx.golf
          </a>
        </p>
      </div>
    );
  }

  // Show saved payment methods or initialize payment
  if (!isPaymentInitialized && !showAddNewCard) {
    return (
      <div style={styles.form}>
        {/* Payment Summary */}
        <div style={styles.paymentSummary}>
          <div style={styles.summaryRow}>
            <span style={styles.summaryLabel}>Amount</span>
            <span style={styles.summaryValue}>${(amount / 100).toFixed(2)} {currency.toUpperCase()}</span>
          </div>
          {email && (
            <div style={styles.summaryRow}>
              <span style={styles.summaryLabel}>Email</span>
              <span style={styles.summaryValue}>{email}</span>
            </div>
          )}
        </div>

        {savedPaymentMethods.length > 0 ? (
          <>
            <p style={styles.sectionLabel}>Select a payment method</p>
            <div style={styles.paymentMethodsList}>
              {savedPaymentMethods.map((pm) => (
                <div
                  key={pm.id}
                  style={{
                    ...styles.paymentMethodCard,
                    ...(selectedPaymentMethodId === pm.id ? styles.paymentMethodCardSelected : {}),
                  }}
                  onClick={() => setSelectedPaymentMethodId(pm.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setSelectedPaymentMethodId(pm.id);
                    }
                  }}
                >
                  <CreditCard size={24} weight="fill" />
                  <div style={styles.paymentMethodInfo}>
                    <span style={styles.paymentMethodBrand}>
                      {pm.brand?.toUpperCase()} •••• {pm.last4}
                    </span>
                    <span style={styles.paymentMethodExpiry}>
                      Expires {pm.expMonth}/{pm.expYear}
                    </span>
                  </div>
                  {selectedPaymentMethodId === pm.id && (
                    <CheckCircle size={20} color="#10b981" weight="fill" />
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowAddNewCard(true)}
              style={styles.addNewCardButton}
            >
              <Plus size={20} weight="bold" />
              Add New Card
            </button>

            <button
              onClick={handlePayWithSavedCard}
              disabled={!selectedPaymentMethodId}
              style={{
                ...styles.button,
                opacity: selectedPaymentMethodId ? 1 : 0.6,
              }}
            >
              Pay with Selected Card
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsPaymentInitialized(true)}
            style={styles.button}
          >
            Add Payment Method
          </button>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.form}>
        <p style={styles.errorMessage}>Failed to load payment form. Please try again.</p>
        <button
          onClick={() => {
            setIsPaymentInitialized(false);
            setTimeout(() => setIsPaymentInitialized(true), 100);
          }}
          style={styles.button}
        >
          Retry
        </button>
      </div>
    );
  }

  if (isLoading || !clientSecret) {
    return <p style={styles.loading}>Loading payment form…</p>;
  }

  // Payment already succeeded — render nothing so Stripe <Elements> is fully
  // unmounted (no Stripe form/CTA/dev toolbar on the success page). The parent
  // checkout shows its own processing overlay and navigates to the success page.
  if (paymentCompleted) {
    return null;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#000000",
            borderRadius: "8px",
          },
        },
        assistant: { enabled: false },
      } as any}
    >
      <CheckoutForm
        email={email}
        submitLabel={submitLabel}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={onPaymentError}
      />
    </Elements>
  );
}

const styles = {
  form: {
    padding: "24px 20px",
    fontFamily: "sans-serif",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
  },
  emailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "14px 16px",
    background: "#f5f5f5",
    borderRadius: 8,
    marginBottom: 24,
    fontSize: 14,
  },
  emailLabel: { color: "#888" },
  emailValue: { color: "#111" },
  sectionLabel: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 12,
    color: "#111",
  },
  paymentElementWrapper: {
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  button: {
    width: "100%",
    padding: "16px",
    background: "#000",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: 16,
    transition: "opacity 0.2s",
  },
  disclaimer: {
    fontSize: 12,
    color: "#888",
    textAlign: "center" as const,
    marginBottom: 12,
    lineHeight: 1.5,
  },
  poweredBy: {
    fontSize: 12,
    color: "#aaa",
    textAlign: "center" as const,
  },
  error: {
    color: "#e53e3e",
    fontSize: 13,
    marginBottom: 12,
  },
  errorCenter: {
    color: "#e53e3e",
    textAlign: "center" as const,
    padding: 40,
  },
  loading: {
    padding: 40,
    textAlign: "center" as const,
    color: "#888",
  },
  successIcon: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 24,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "#111",
    marginBottom: 12,
    textAlign: "center" as const,
  },
  successMessage: {
    fontSize: 16,
    color: "#666",
    marginBottom: 24,
    textAlign: "center" as const,
    lineHeight: 1.5,
  },
  paymentDetails: {
    background: "#f9fafb",
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
  },
  detailLabel: {
    fontSize: 12,
    color: "#888",
    marginBottom: 4,
    textAlign: "center" as const,
  },
  detailValue: {
    fontSize: 13,
    color: "#111",
    fontFamily: "monospace",
    wordBreak: "break-all" as const,
    textAlign: "center" as const,
  },
  errorIcon: {
    display: "flex",
    justifyContent: "center",
    marginBottom: 24,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "#111",
    marginBottom: 12,
    textAlign: "center" as const,
  },
  errorMessage: {
    fontSize: 16,
    color: "#ef4444",
    marginBottom: 24,
    textAlign: "center" as const,
    lineHeight: 1.5,
  },
  supportLink: {
    color: "#3b82f6",
    textDecoration: "underline",
    cursor: "pointer",
  },
  paymentMethodsList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 12,
    marginBottom: 16,
  },
  paymentMethodCard: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 16,
    border: "2px solid #e0e0e0",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.2s",
  },
  paymentMethodCardSelected: {
    borderColor: "#10b981",
    background: "#f0fdf4",
  },
  paymentMethodInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
  },
  paymentMethodBrand: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111",
  },
  paymentMethodExpiry: {
    fontSize: 12,
    color: "#888",
  },
  addNewCardButton: {
    width: "100%",
    padding: "12px",
    background: "#fff",
    color: "#000",
    border: "2px dashed #ccc",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    marginBottom: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "all 0.2s",
  },
  paymentSummary: {
    background: "#f9fafb",
    borderRadius: 8,
    padding: 16,
    marginBottom: 24,
    border: "1px solid #e5e7eb",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
  },
  summaryLabel: {
    fontSize: 14,
    color: "#6b7280",
    fontWeight: 500,
  },
  summaryValue: {
    fontSize: 16,
    color: "#111827",
    fontWeight: 600,
  },
};
